// TODO: Hardik cleanup pending
import config from 'config';
import _ from 'lodash';

import { DESTINATION_CHANNELS, SOURCE_ORIGIN } from 'config/common';
import phoneConfig from 'config/phone';
import { PAGE_NAME } from 'lib/gtm';
import {
  COOKIE_MAP,
  getCLIDFromCookie,
  getCookie,
  getMSCLKIDFromCookie,
  setCookie
} from 'services/cookie.service';
import { getPageURL, ROUTE_CONFIG } from 'services/hostPortal.service';
import { getUserAuth } from 'services/identity.service';
import { createLeadMarketingReferences } from 'services/user.service';

const { hebaWhatsapp } = phoneConfig.phoneNumbers;

export const HEBA_PAGE_ROUTE = {
  WHATSAPP: 'WHATSAPP',
  WEBCHAT_V1: 'WEBCHAT_V1', // plan-event-with-heba        (iframe-based, working)
  WEBCHAT_V2: 'WEBCHAT_V2' // plan-event-with-heba-webchat (direct-script, experimental)
};

// Generic CTAs (not WhatsApp-branded icons) read from here.
// To flip the whole site from WhatsApp to corresponding webchat, change this one line.
export const GENERIC_HEBA_PAGE_ROUTE = HEBA_PAGE_ROUTE.WHATSAPP;

const slugsToExcludeForBookingPortal = [
  'about-us',
  'contact',
  'furniture-testing',
  'gallery',
  'join-our-team',
  'pushdaddy-faq-1',
  'request-a-call',
  'request-a-quote',
  'sample-page-for-training',
  'terms-of-service',
  'venue-inquiry-submission'
];

const getLeadMarketingReferencesFromUserAuth = ({ userAuth }) => ({
  guestUserId: userAuth?.isGuest ? userAuth.id : null,
  hostId: userAuth?.isGuest ? null : userAuth?.id
});

const BOOKING_PAGE_NAME = {
  COLLECTION_HOME: {
    label: 'COLLECTION_HOME',
    regex: /^\/collections\/./
  },
  PAGES: { label: 'PAGES', regex: /^\/pages\/./ },
  PDP: { label: 'PDP', regex: /^\/products\/./ }
};

const getLeadMarketingReferencesFromQueryParameters = ({ query }) => ({
  fbclid: query.fbclid,
  gclid: query.gclid,
  liid: query.li_fat_id,
  msclkid: query.msclkid,
  scclid: query.scclid,
  sourceOrigin: query.sourceOrigin || SOURCE_ORIGIN.HOST_PORTAL,
  sourcePathname: query.sourcePathname,
  ttclid: query.ttclid,
  twclid: query.twclid,
  utmCampaign: query.utm_campaign,
  utmContent: query.utm_content,
  utmMedium: query.utm_medium,
  utmSource: query.utm_source,
  utmTerm: query.utm_term,
  ytclid: query.ytclid
});

const getLeadMarketingReferencesFromCookie = () => ({
  fbclid:
    getCLIDFromCookie({ name: COOKIE_MAP.FBC }) ||
    getCLIDFromCookie({ name: COOKIE_MAP.FBP }),
  gclid:
    getCLIDFromCookie({ name: COOKIE_MAP.GCL_AW }) ||
    getCLIDFromCookie({ name: COOKIE_MAP.GCL_AU }),
  liid: getCLIDFromCookie({ name: COOKIE_MAP.LIID }),
  msclkid: getMSCLKIDFromCookie(),
  scclid: getCLIDFromCookie({ name: COOKIE_MAP.SCLID }),
  ttclid: getCLIDFromCookie({ name: COOKIE_MAP.TTCLID })
});

const removeNullUndefinedEmptyStringValuesFromJSONObject = (obj) => {
  if (_.isArray(obj)) {
    return obj
      .map(removeNullUndefinedEmptyStringValuesFromJSONObject)
      .filter((v) => !_.isNil(v) && v !== '');
  }
  if (_.isObject(obj)) {
    return _.omitBy(
      _.mapValues(obj, removeNullUndefinedEmptyStringValuesFromJSONObject),
      (v) => _.isNil(v) || v === ''
    );
  }
  return obj;
};

const getLeadMarketingReferenceNumber = async ({
  destinationChannel,
  query,
  setShowToast
}) => {
  const userAuth = getUserAuth();
  const sourceOrigin = query.sourceOrigin || SOURCE_ORIGIN.HOST_PORTAL;

  const [
    leadMarketingReferencesFromQuery,
    leadMarketingReferencesFromCookie,
    leadMarketingReferencesFromUserAuth
  ] = [
    getLeadMarketingReferencesFromQueryParameters({ query }),
    getLeadMarketingReferencesFromCookie(),
    getLeadMarketingReferencesFromUserAuth({ userAuth })
  ].map((leadMarketingReferences) =>
    removeNullUndefinedEmptyStringValuesFromJSONObject(leadMarketingReferences)
  );

  const getHostPortalLeadMarketingReferences = () => ({
    ...leadMarketingReferencesFromUserAuth,
    ...leadMarketingReferencesFromCookie,
    ...leadMarketingReferencesFromQuery
  });

  const leadMarketingReferences =
    sourceOrigin === SOURCE_ORIGIN.HOST_PORTAL
      ? getHostPortalLeadMarketingReferences()
      : leadMarketingReferencesFromQuery;

  if (destinationChannel) {
    leadMarketingReferences.destinationChannel = destinationChannel;
  }

  const {
    referenceNumber: storedReferenceNumber,
    ...storedLeadMarketingReferences
  } =
    getCookie({
      name: COOKIE_MAP.LEAD_MARKETING_REFERENCES
    }) || {};

  const reuseLeadMarketingReference =
    storedReferenceNumber &&
    _.isEqual(storedLeadMarketingReferences, leadMarketingReferences);

  if (reuseLeadMarketingReference) {
    return null;
  }

  try {
    const { entity, message, status } = await createLeadMarketingReferences({
      leadMarketingReferences
    });
    const referenceNumber = entity?.leadMarketingReferenceNumber;
    const failToGetLeadMarketingReference = !status || !referenceNumber;
    if (failToGetLeadMarketingReference) {
      setShowToast({
        message: `Failed to create Lead Marketing Reference : ${message}`,
        show: true,
        status: false
      });
      return null;
    }

    setCookie({
      data: { ...leadMarketingReferences, referenceNumber },
      name: COOKIE_MAP.LEAD_MARKETING_REFERENCES
    });

    return referenceNumber;
  } catch (error) {
    setShowToast({
      message: `Failed to create Lead Marketing Reference : ${error.message}`,
      show: true,
      status: false
    });
  }

  return null;
};

const getLeadMarketingReferenceTracker = async ({
  leadMarketingReferenceNumber
}) =>
  leadMarketingReferenceNumber
    ? `\n\nReferenceCode: ${leadMarketingReferenceNumber}\n`
    : '';

const getPageLabelFromPathname = (pathname) =>
  Object.values(PAGE_NAME).find((page) => page.regex.test(pathname))?.label ??
  '';

const getBookingPageLabelFromPathname = (pathname) =>
  Object.values(BOOKING_PAGE_NAME).find((page) => page.regex.test(pathname))
    ?.label ?? '';

const getMessageForClpPlp = ({ categorySlug, parentSlug, subCategorySlug }) => {
  if (subCategorySlug) {
    return `Hi Heba, I am exploring ${subCategorySlug} within category ${categorySlug} for an upcoming event`;
  }
  if (categorySlug) {
    return `Hi Heba, I am exploring ${categorySlug} within category ${parentSlug} for an upcoming event`;
  }
  return `Hi Heba, I am exploring ${parentSlug} for an upcoming event`;
};

const getMessageForBookingPortalPages = ({ slugForBookingPortal }) => {
  if (slugsToExcludeForBookingPortal.includes(slugForBookingPortal)) {
    return `Hi Heba!`;
  }
  return `Hi Heba, I am exploring ${slugForBookingPortal} for an upcoming event`;
};

const getHelloMessageBodyForHostPortal = ({ query }) => {
  const {
    categorySlug = '',
    parentSlug = '',
    slug = '',
    sourcePathname,
    subCategorySlug = ''
  } = query;
  const pageLabelFromPathName = getPageLabelFromPathname(sourcePathname);
  const messageForClpPlp = getMessageForClpPlp({
    categorySlug,
    parentSlug,
    subCategorySlug
  });

  let message;
  switch (pageLabelFromPathName) {
    case PAGE_NAME.CLP_AND_PLP.label:
      message = messageForClpPlp;
      break;
    case PAGE_NAME.PDP.label:
      message = `Hi Heba, I am exploring ${slug} for an upcoming event`;
      break;
    default:
      message = 'Hi Heba!';
      break;
  }
  return message;
};

const getHelloMessageBodyForBookingPortal = ({ sourcePathname }) => {
  const pageLabelFromPathName = getBookingPageLabelFromPathname(sourcePathname);
  const slugForBookingPortal = sourcePathname.split('/').pop();

  let message;
  switch (pageLabelFromPathName) {
    case BOOKING_PAGE_NAME.COLLECTION_HOME.label:
    case BOOKING_PAGE_NAME.PDP.label:
      message = `Hi Heba, I am exploring ${slugForBookingPortal} for an upcoming event`;
      break;
    case BOOKING_PAGE_NAME.PAGES.label:
      message = getMessageForBookingPortalPages({ slugForBookingPortal });
      break;
    default:
      message = 'Hi Heba!';
      break;
  }
  return message;
};

const getHelloMessageBody = ({ query }) => {
  const { sourceOrigin, sourcePathname } = query;
  let message;
  switch (sourceOrigin) {
    case SOURCE_ORIGIN.CORPORATE_PORTAL:
      message = 'Hi Heba! I need help in planning a corporate event.';
      break;
    case SOURCE_ORIGIN.WEDDINGS_PORTAL:
      message =
        'Hi Heba! I need help in organising a wedding/pre-wedding celebration.';
      break;
    case SOURCE_ORIGIN.HOST_PORTAL:
      message = getHelloMessageBodyForHostPortal({ query });
      break;
    case SOURCE_ORIGIN.BOOKING_PORTAL:
      message = getHelloMessageBodyForBookingPortal({ sourcePathname });
      break;
    default:
      message = 'Hi Heba!';
      break;
  }
  return message;
};

const getWelcomeMessageForBotpressHeba = ({ marketingLeadSuffix, query }) => {
  const messagePrefix = config.isProduction
    ? ''
    : `Please Ignore this - this message is from the non production portal. \n\n`;

  const message = getHelloMessageBody({ query });

  return `${messagePrefix}${message}${marketingLeadSuffix}`;
};

const generateWelcomeMessageForBotpressHeba = async ({
  destinationChannel,
  query,
  setShowToast
}) => {
  const leadMarketingReferenceNumber = await getLeadMarketingReferenceNumber({
    destinationChannel,
    query,
    setShowToast
  });

  const marketingLeadSuffix = await getLeadMarketingReferenceTracker({
    leadMarketingReferenceNumber
  });

  const formattedMessage = getWelcomeMessageForBotpressHeba({
    marketingLeadSuffix,
    query
  });

  return formattedMessage;
};

export const sendBotpressHebaWelcomeMessageToWebchat = async ({
  query,
  setIsLeadMarketingReferencePosted,
  setShowToast
}) => {
  const welcomeMessage = await generateWelcomeMessageForBotpressHeba({
    destinationChannel: DESTINATION_CHANNELS.WEB_CHAT,
    query,
    setShowToast
  });
  let intervalId;
  const attemptSendingMessageTillSuccess = async () => {
    try {
      await window.botpress.sendMessage(welcomeMessage);
      setIsLeadMarketingReferencePosted(true);
      clearInterval(intervalId);
    } catch (e) {
      console.error(
        'heba error in botpress.sendMessage - window.botpress',
        window.botpress,
        e?.message
      );
    }
  };
  intervalId = setInterval(attemptSendingMessageTillSuccess, 1500);
};

export const getBotpressHebaWABARedirectURL = async ({
  query,
  setShowToast
}) => {
  const whatsAppBaseURL = 'https://api.whatsapp.com/send';

  const formattedMessage = await generateWelcomeMessageForBotpressHeba({
    destinationChannel: DESTINATION_CHANNELS.WHATSAPP,
    query,
    setShowToast
  });

  return `${whatsAppBaseURL}?phone=${hebaWhatsapp}&text=${encodeURIComponent(
    formattedMessage
  )}`;
};

const buildHebaURL = ({ pageName, params, pathname, searchParams }) => {
  const query =
    searchParams instanceof URLSearchParams
      ? Object.fromEntries(searchParams.entries())
      : searchParams || {};

  return getPageURL({
    pageName,
    queryParams: {
      ...params,
      ...query,
      sourceOrigin: SOURCE_ORIGIN.HOST_PORTAL,
      sourcePathname: pathname
    }
  });
};

const HEBA_PAGE_NAME = {
  [HEBA_PAGE_ROUTE.WHATSAPP]: ROUTE_CONFIG.PLAN_EVENT_WITH_HEBA_WHATSAPP.label,
  [HEBA_PAGE_ROUTE.WEBCHAT_V1]: ROUTE_CONFIG.PLAN_EVENT_WITH_HEBA.label,
  [HEBA_PAGE_ROUTE.WEBCHAT_V2]: ROUTE_CONFIG.PLAN_EVENT_WITH_HEBA_WEBCHAT.label
};

export const getPlanEventWithHebaURL = ({
  pageRoute,
  params,
  pathname,
  searchParams
}) =>
  buildHebaURL({
    pageName: HEBA_PAGE_NAME[pageRoute],
    params,
    pathname,
    searchParams
  });
